import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { removeSubmissionMemberApi } from "./submissionStorage";

const MUTED = "#64748b";
const MESSAGE_MAX = 2000;
const REASON_MAX = 200;
const NOTE_MAX = 500;

const CHANNELS = {
  email: {
    label: "Email",
    sent: "Goodbye email sent",
    failed: "Goodbye email not sent",
  },
  inApp: {
    label: "In-app",
    sent: "In-app notification sent",
    failed: "In-app notification not sent",
  },
};

const defaultMessage = (firstName, journeyName) => {
  const who = firstName || "there";
  const journey = journeyName || "this journey";
  return `Hi ${who}, you've been removed from ${journey}. If you have any questions, please contact your coach.`;
};

const getFirstName = (person = {}) => {
  if (person.firstName) return String(person.firstName).trim();
  const name = String(person.name || person.memberName || "").trim();
  if (!name || name === "Unknown" || name.includes("@")) return "";
  return name.split(/\s+/)[0];
};

/**
 * Drop a person out of the journey from the People tab (stays on screen).
 * Reason is required so coaches record why they exited.
 */
export default function DropOutPersonDialog({
  open,
  person,
  trackerName = "",
  onClose,
  onDropped,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const submissionId = person?.latestSubmissionId || person?.id || "";
  const emailAvailable = Boolean(person?.email);
  const inAppAvailable = Boolean(person?.memberId);
  const firstName = getFirstName(person || {});
  const journeyName = trackerName || person?.trackerName || "";

  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [emailChecked, setEmailChecked] = useState(true);
  const [inAppChecked, setInAppChecked] = useState(true);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setNote("");
    setMessage(defaultMessage(firstName, journeyName));
    setEmailChecked(true);
    setInAppChecked(true);
    setRemoving(false);
  }, [open, submissionId, firstName, journeyName]);

  const wantEmail = emailAvailable && emailChecked;
  const wantInApp = inAppAvailable && inAppChecked;
  const wantsNotify = wantEmail || wantInApp;
  const reasonOk = Boolean(reason.trim());
  const messageOk = !wantsNotify || Boolean(message.trim());
  const canRemove = Boolean(submissionId) && reasonOk && messageOk && !removing;

  const notifyResults = (result) => {
    if (!result.results) return;
    [
      ["email", wantEmail],
      ["inApp", wantInApp],
    ].forEach(([key, requested]) => {
      if (!requested) return;
      const outcome = result.results[key];
      if (outcome?.sent) {
        enqueueSnackbar(CHANNELS[key].sent, { variant: "success" });
        return;
      }
      enqueueSnackbar(
        outcome?.error
          ? `${CHANNELS[key].failed}: ${outcome.error}`
          : CHANNELS[key].failed,
        { variant: "warning" }
      );
    });
  };

  const handleConfirm = async () => {
    if (!canRemove) return;
    setRemoving(true);
    const result = await removeSubmissionMemberApi(submissionId, {
      reason: reason.trim(),
      note: note.trim(),
      message: wantsNotify ? message.trim() : "",
      notifyEmail: wantEmail,
      notifyInApp: wantInApp,
    });
    setRemoving(false);
    if (!result.ok) {
      enqueueSnackbar(result.message || "Couldn't drop them out", {
        variant: "error",
      });
      return;
    }
    if (result.alreadyRemoved) {
      enqueueSnackbar("They were already off this journey", {
        variant: "info",
      });
    } else {
      enqueueSnackbar("Dropped out of the journey", { variant: "success" });
      notifyResults(result);
    }
    if (onDropped) await onDropped(result.submission);
    onClose?.();
  };

  const personLabel =
    person?.name || person?.memberName || person?.email || "this person";

  return (
    <Dialog
      open={open}
      onClose={removing ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        sx: { borderRadius: "14px" },
      }}
    >
      <DialogTitle sx={{ pb: 0.5, fontWeight: 700, fontSize: 18 }}>
        Drop out of journey
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: MUTED, mb: 2 }}>
          Mark <strong>{personLabel}</strong> as Dropped Out. This stops
          reminders and automation emails. Tell us why they are leaving.
        </Typography>

        <TextField
          autoFocus
          fullWidth
          size="small"
          required
          label="Reason"
          value={reason}
          disabled={removing}
          placeholder="e.g. Not engaging, requested to leave, wrong fit"
          onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
          inputProps={{ maxLength: REASON_MAX }}
          helperText={
            reasonOk
              ? `${reason.length}/${REASON_MAX}`
              : "Required — why are they dropping out?"
          }
          FormHelperTextProps={{ sx: { textAlign: "left", mx: 0 } }}
          sx={{ mb: 1.5 }}
        />

        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Internal note (optional)"
          value={note}
          disabled={removing}
          placeholder="Only visible to coaches"
          onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
          inputProps={{ maxLength: NOTE_MAX }}
          sx={{ mb: 1.5 }}
        />

        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Message to member"
          value={message}
          disabled={removing || !wantsNotify}
          placeholder="Goodbye message…"
          onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
          inputProps={{ maxLength: MESSAGE_MAX }}
          helperText={
            wantsNotify
              ? `${message.length}/${MESSAGE_MAX}`
              : "Turn on email or in-app to send a goodbye"
          }
          FormHelperTextProps={{
            sx: { textAlign: wantsNotify ? "right" : "left", mx: 0 },
          }}
          sx={{ mb: 1 }}
        />

        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={wantEmail}
                  disabled={!emailAvailable || removing}
                  onChange={(e) => setEmailChecked(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: 13.5 }}>Send email</Typography>
              }
            />
            {!emailAvailable ? (
              <Typography sx={{ fontSize: 12, color: MUTED, ml: 4, mt: -0.75 }}>
                No email on file
              </Typography>
            ) : null}
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={wantInApp}
                  disabled={!inAppAvailable || removing}
                  onChange={(e) => setInAppChecked(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: 13.5 }}>
                  Send in-app notification
                </Typography>
              }
            />
            {!inAppAvailable ? (
              <Typography sx={{ fontSize: 12, color: MUTED, ml: 4, mt: -0.75 }}>
                No portal account
              </Typography>
            ) : null}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <button
          type="button"
          className="small-contained-outlined-button"
          disabled={removing}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="small-contained-button"
          disabled={!canRemove}
          onClick={handleConfirm}
          style={{ backgroundColor: "#dc2626" }}
        >
          {removing ? (
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
            >
              <CircularProgress size={14} color="inherit" />
              Dropping out…
            </Box>
          ) : (
            "Confirm drop out"
          )}
        </button>
      </DialogActions>
    </Dialog>
  );
}
